import {
  OmakasePolymorphicComponent,
  OmakasePolymorphicComponentProps,
  OmakasePolymorphicRootComponent,
} from '../utils/OmakasePolymorphicComponent';
import { Theme } from '@emotion/react';
import { chipLabelBase } from 'omakase/ChipLabel/chipLabelCssDefs';
import React from 'react';

export type ChipLabelSize = 'default' | 'medium' | 'large';
export type ChipLabelVariant =
  | 'basic'
  | 'ghost'
  | 'success'
  | 'danger'
  | 'warning'
  | 'information'
  | 'subtle';

export interface ChipLabelProps {
  /**
   * The size of the component
   */
  size?: ChipLabelSize;
  /**
   * The design variant for the component
   */
  variant: ChipLabelVariant;
  /**
   * Whether or not the chip label variant is in "high contrast" mode
   */
  highContrast?: boolean;
}

const ChipLabelDefaultElement = 'span';
type ChipLabelTypeGuard = 'span' | 'div';

const ChipLabel: OmakasePolymorphicComponent<
  ChipLabelProps,
  typeof ChipLabelDefaultElement,
  ChipLabelTypeGuard
> = React.forwardRef(
  <E extends React.ElementType = typeof ChipLabelDefaultElement>(
    {
      component,
      highContrast = false,
      size = 'default',
      variant,
      ...rest
    }: OmakasePolymorphicComponentProps<ChipLabelProps, E, ChipLabelTypeGuard>,
    ref: React.Ref<HTMLSpanElement> | undefined
  ) => {
    const Component: React.ElementType = component || ChipLabelDefaultElement;

    return (
      <OmakasePolymorphicRootComponent
        css={(theme: Theme) => chipLabelBase(theme, size, variant, { highContrast })}
        ref={ref}
        component={Component}
        {...rest}
      />
    );
  }
);

export default ChipLabel;
